/**
 * TEAM: customs_platform
 *
 * Latitude Design System Theme Integration
 *
 * This file imports and exports Latitude design tokens (colors, typography, spacing)
 * for consistent use throughout the File Uploader application.
 */

import { colors, typeScale } from "@flexport/latitude-typescript";

// Re-export Latitude design tokens for application-wide use
export { colors, typeScale };

// Font family and weights based on Latitude design system
// These are not exported from the package but are part of the design system
export const uiFontFamily =
  '"Proxima Nova", "Helvetica Neue", Helvetica, Arial, sans-serif';

export const fontWeights = {
  regular: "400" as const,
  medium: "500" as const,
  bold: "700" as const,
  boldExtended: "800" as const,
};

/**
 * Semantic color mappings for the File Uploader application
 * Maps Latitude colors to semantic names for easier use in components
 */
export const semanticColors = {
  // Text colors
  textPrimary: colors.grey60,
  textSecondary: colors.grey50,
  textLight: colors.grey40,
  textOnDark: colors.white,

  // Background colors
  backgroundPrimary: colors.white,
  backgroundSecondary: colors.grey10,
  backgroundHover: colors.grey20,
  backgroundDark: colors.grey60,

  // Border colors
  borderLight: colors.grey20,
  borderMedium: colors.grey30,
  borderDark: colors.grey40,

  // Interactive colors
  primary: colors.blue50,
  primaryHover: colors.blue40,
  primaryLight: colors.blue20,

  // Status colors
  success: colors.green50,
  successLight: colors.green20,
  warning: colors.orange50,
  warningLight: colors.orange20,
  error: colors.red50,
  errorLight: colors.red20,

  // Special colors
  info: colors.blue30,
  highlight: colors.blue10,
} as const;

/**
 * Typography scale helpers
 * Provides easy access to Latitude typography scales
 */
export const typography = {
  display: typeScale.display,
  headline: typeScale.headline,
  title: typeScale.title,
  base: typeScale.base,
  subtext: typeScale.subtext,
  tiny: typeScale.tiny,
  large: typeScale.large,
} as const;
